import { Named } from '../base/named';
import { Copier } from '../base/copier';
import { Shadow } from './shadow';
import { Injectable } from '@angular/core';
import { ResourceType } from 'app/services/globals';
import { A365Named, Metamodel } from 'shell-models-lib';
import { ModelService } from './model.service';

interface IUndoRedos {
    undos: Named[];
    redos: Named[];
}

@Injectable({
    providedIn: 'root'
})
export class UndoRedoService {
    private _namedList: Map<Named, IUndoRedos> = new Map();

    constructor(private readonly _modelService: ModelService) {}

    public clearState(): void {
        this._namedList.clear();
    }

    public clearStateForResource(resourceType: ResourceType, parentName?: string): void {
        const nameds = this._namedList.keys();
        for (const named of nameds) {
            if (named && named instanceof A365Named) {
                const concept = Metamodel.modelOf(named).concept;
                const namedType = concept ? concept.name : undefined;
                if (resourceType === namedType) {
                    if (parentName) {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        if ((named as any).parentInfo && (named as any).parentInfo.resourceName
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            && (named as any).parentInfo.resourceName.toLowerCase() === parentName.toLowerCase()) {
                            this._namedList.delete(named);
                        }
                    } else {
                        this._namedList.delete(named);
                    }
                }
            }
        }
    }

    public clearUndoRedoHistory(named: Named): void {
        if (this._namedList.has(named)) {
            this._namedList.delete(named);
        }
    }

    public addUndoState(named: Named): void {
        // When user adds a new UNDO state, clear redo list.
        // Redo list is only filled with UNDO actions.
        this._addUndoState(named);
        this._namedList.get(named).redos.length = 0;
    }

    public undo(named: Named): void {
        const undoRedos = this._namedList.get(named);

        if (!undoRedos) {
            return;
        }

        if (undoRedos.undos.length > 0) {
            // This is current state, so put the current state in the redo list.
            // whenever there is a change in the object we push that to undo list.
            undoRedos.redos.push(undoRedos.undos.pop());

            // This is the state we should make current now, as this was last state before latest user action.
            if (undoRedos.undos.length > 0) {
                Copier.clone(this._modelService.modelAttributeContext, [undoRedos.undos[undoRedos.undos.length - 1]], [named], false, true);
                return;
            }
        }

        // If can't find any last state then make shadow as current state.
        const shadow = Shadow.shadow(named);
        Copier.clone(this._modelService.modelAttributeContext, [shadow], [named], false, true);
    }

    public redo(named: Named): void {
        const undoRedos = this._namedList.get(named);

        if (!undoRedos) {
            return;
        }

        if (undoRedos.redos.length > 0) {
            const redoNamed = undoRedos.redos.pop();
            // If redo state is there then push the that to the undolist,
            // as that becomes the current state. Remember current state is
            // always on top of undo list.
            undoRedos.undos.push(Copier.clone(this._modelService.modelAttributeContext, [redoNamed], undefined, false, true)[0]);

            Copier.clone(this._modelService.modelAttributeContext, [redoNamed], [named], false, true);
        }
    }

    public getNamedList(): Map<Named, IUndoRedos> {
        return this._namedList;
    }

    private _addUndoState(named: Named): void {
        if (!this._namedList.has(named)) {
            this._namedList.set(named, { undos: [], redos: [] });
        }

        this._namedList.get(named).undos.push(Copier.clone(this._modelService.modelAttributeContext, [named])[0]);
    }
}
